import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Check } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Logo360 } from "@/components/Logo";
import { PillGroup, InfoBox } from "@/components/ui-kit";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { languages, currencies } from "@/i18n";

const inputClass = "w-full bg-brand-ivory/50 border-brand-line rounded-xl px-4 py-3 h-auto focus-visible:ring-2 focus-visible:ring-brand-champagne";

export default function OnboardingPage() {
  const { t, changeLanguage, updateProfile, apiError, lang } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [data, setData] = useState({ currency: "EUR", vat_enabled: false, vat_rate: 22, hourly_value: "" });
  const [saving, setSaving] = useState(false);
  const total = 4;

  const finish = async () => {
    setSaving(true);
    try {
      await updateProfile({
        currency: data.currency,
        vat_enabled: data.vat_enabled,
        vat_rate: Number(data.vat_rate) || 0,
        hourly_value: Number(data.hourly_value) || 0,
        onboarding_completed: true,
      });
      localStorage.setItem("currency", data.currency);
      navigate("/");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const next = () => (step < total - 1 ? setStep(step + 1) : finish());

  return (
    <div className="min-h-screen bg-brand-ivory flex items-center justify-center p-5">
      <div className="w-full max-w-lg">
        <div className="flex flex-col items-center text-center mb-8">
          <Logo360 size={56} />
          <h1 className="mt-4 font-serif text-3xl font-medium text-brand-anthracite">{t("onboarding.title")}</h1>
          <p className="mt-1 text-brand-taupe">{t("onboarding.subtitle")}</p>
        </div>

        <div className="mb-6 flex items-center gap-2">
          {Array.from({ length: total }).map((_, i) => (
            <div key={i} className={`h-1.5 flex-1 rounded-full transition-colors ${i <= step ? "bg-brand-champagne" : "bg-brand-line"}`} />
          ))}
        </div>

        <div className="bg-white rounded-2xl border border-brand-line p-6 sm:p-8 shadow-[0_4px_20px_rgba(184,169,154,0.08)] animate-fade-up" key={step}>
          <div className="text-xs uppercase tracking-wider text-brand-taupe mb-4">{t("onboarding.stepOf", { n: step + 1, total })}</div>

          {step === 0 && (
            <div className="space-y-5" data-testid="onboarding-step-language">
              <h2 className="font-serif text-2xl text-brand-anthracite">{t("onboarding.langTitle")}</h2>
              <div className="grid gap-3">
                {languages.map((l) => (
                  <button key={l.code} data-testid={`onboarding-lang-${l.code}`} onClick={() => changeLanguage(l.code)}
                    className={`flex items-center justify-between rounded-xl border px-4 py-3 text-left transition-all ${lang === l.code ? "border-brand-champagne bg-brand-champagne/15" : "border-brand-line hover:border-brand-taupe"}`}>
                    <span className="font-medium">{l.label}</span>
                    {lang === l.code && <Check className="h-5 w-5 text-brand-champagne" />}
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-5" data-testid="onboarding-step-currency">
              <h2 className="font-serif text-2xl text-brand-anthracite">{t("onboarding.currencyTitle")}</h2>
              <p className="text-sm text-brand-taupe">{t("onboarding.currencySubtitle")}</p>
              <div className="grid grid-cols-2 gap-3">
                {currencies.map((c) => (
                  <button key={c.code} data-testid={`onboarding-currency-${c.code}`} onClick={() => setData({ ...data, currency: c.code })}
                    className={`rounded-xl border px-4 py-6 text-center transition-all ${data.currency === c.code ? "border-brand-champagne bg-brand-champagne/15" : "border-brand-line hover:border-brand-taupe"}`}>
                    <div className="font-serif text-3xl text-brand-anthracite">{c.symbol}</div>
                    <div className="mt-1 text-sm font-medium">{c.code}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5" data-testid="onboarding-step-vat">
              <h2 className="font-serif text-2xl text-brand-anthracite">{t("onboarding.vatTitle")}</h2>
              <p className="text-sm text-brand-taupe">{t("onboarding.vatSubtitle")}</p>
              <div className="flex items-center justify-between rounded-xl border border-brand-line px-4 py-3">
                <span className="font-medium">{t("onboarding.vatApply")}</span>
                <Switch checked={data.vat_enabled} onCheckedChange={(v) => setData({ ...data, vat_enabled: v })} data-testid="onboarding-vat-switch" />
              </div>
              {data.vat_enabled && (
                <div className="space-y-2">
                  <Label>{t("onboarding.vatRateLabel")} (%)</Label>
                  <Input type="number" min="0" max="100" value={data.vat_rate} onChange={(e) => setData({ ...data, vat_rate: e.target.value })} className={inputClass} data-testid="onboarding-vat-rate" />
                </div>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="space-y-5" data-testid="onboarding-step-hourly">
              <h2 className="font-serif text-2xl text-brand-anthracite">{t("onboarding.hourlyTitle")}</h2>
              <div className="space-y-2">
                <Label>{t("onboarding.hourlyLabel")}</Label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-brand-taupe">{currencies.find((c) => c.code === data.currency)?.symbol}</span>
                  <Input type="number" min="0" step="0.5" value={data.hourly_value} onChange={(e) => setData({ ...data, hourly_value: e.target.value })} className={`${inputClass} pl-8`} data-testid="onboarding-hourly" placeholder="25" autoFocus />
                </div>
              </div>
              <InfoBox tone="blush">{t("onboarding.hourlyHelp")}</InfoBox>
            </div>
          )}

          <div className="mt-8 flex items-center justify-between">
            <button onClick={() => step > 0 && setStep(step - 1)} disabled={step === 0} className="text-sm text-brand-taupe disabled:opacity-0" data-testid="onboarding-back">{t("common.back")}</button>
            <Button onClick={next} disabled={saving} data-testid="onboarding-next" className="rounded-xl bg-brand-anthracite text-brand-ivory hover:bg-brand-anthracite/90 px-6 h-11">
              {step === total - 1 ? t("onboarding.getStarted") : t("common.continue")}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
